import React, { useState, useMemo, useEffect } from "react";
import { Alert, Stack, Chip, CircularProgress, TextField } from "@mui/material";
import MDAutocomplete from "components/MDAutocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DraggableDialog from "components/draggagle-dialog";
import WarningIcon from "@mui/icons-material/Warning";
import PersonIcon from "@mui/icons-material/Person";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Delete } from "@mui/icons-material";

// Function to format plain digits to formatted phone number
const formatToPhoneNumber = (number) => {
  const cleaned = number.replace(/\D/g, "").slice(0, 10);
  let maskedValue = "";

  if (cleaned.length <= 3) {
    maskedValue = cleaned;
  } else if (cleaned.length <= 6) {
    maskedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    maskedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  return maskedValue;
};

function TenantAdminDeleteDialog({
  open,
  onClose,
  adminToDelete,
  allAdmins,
  onDelete,
  isDeleting = false,
}) {
  const [entityAssignedTo, setEntityAssignedTo] = useState(null);

  // Filter out the admin being deleted from the reassignment options
  const availableAdmins = useMemo(() => {
    return allAdmins.filter((admin) => admin.id !== adminToDelete?.id);
  }, [allAdmins, adminToDelete?.id]);

  const handleClose = () => {
    setEntityAssignedTo(null);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setEntityAssignedTo(null);
    }
  }, [open]);

  const handleConfirmDelete = () => {
    onDelete(adminToDelete.id, entityAssignedTo);
  };

  const renderDialogContent = () => {
    return (
      <MDBox>
        <Stack spacing={2}>
          <MDTypography variant="body2">
            This admin has entities assigned to them. Please select another tenant admin to reassign
            all entities before deletion.
          </MDTypography>

          <MDAutocomplete
            options={availableAdmins}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Tenant Admin for Reassignment"
                required
                // helperText="All entities will be transferred to this admin."
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon fontSize="small" color="primary" />
                  <MDTypography variant="body2">{option.label}</MDTypography>
                </Stack>
              </li>
            )}
            onChange={(event, newValue) => {
              setEntityAssignedTo(newValue);
            }}
            value={entityAssignedTo}
            disabled={isDeleting}
          />

          {availableAdmins.length === 0 && (
            <Alert severity="error">
              <MDTypography variant="body2">
                No other tenant admins available for reassignment. You need at least one other admin
                to delete this one.
              </MDTypography>
            </Alert>
          )}
        </Stack>
      </MDBox>
    );
  };

  const renderDialogActions = () => {
    return (
      <MDButton
        variant="outlined"
        color="error"
        onClick={handleConfirmDelete}
        disabled={!entityAssignedTo || isDeleting || availableAdmins.length === 0}
        startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
      >
        {isDeleting ? "Deleting..." : "Delete Admin"}
      </MDButton>
    );
  };

  return (
    <DraggableDialog
      dialogTitle={
        <Stack direction="row" spacing={1} alignItems="center">
          {/* <WarningIcon color="warning" /> */}
          <MDTypography variant="h6">Reassign Admin Entities</MDTypography>
        </Stack>
      }
      open={open}
      onClose={handleClose}
      dialogContent={renderDialogContent()}
      dialogActions={renderDialogActions()}
      maxWidth="md"
    />
  );
}

export default TenantAdminDeleteDialog;
